import { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Tooltip,
  ToggleButton,
  Alert,
  TableRow,
  TableCell,
} from "@mui/material";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  ErrorOutline as ErrorOutlineIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import { ETableEditAction } from "../../Utils/Enums";
import { DocumentsModel } from "../../Utils/Models";
import { Vocabulary, replacePlaceholders } from "../../Utils/Vocabulary";
import { IDocumentsManagerProps } from "../../Utils/Types";
import { FormsIds } from "../../Utils/Constants";
import MUIDataTableCustomStyle from "../MUIDataTableCustomStyle";
import GenericModal from "../GenericModal";
import AddDocumentModal from "./AddDocumentModal";
import GenerateDocumentDropdown from "./GenerateDocumentDropdown";
import { endpoints } from "../../Utils/UrlEnum";
import genericStyles from "../../Styles/genericStyles.module.css";
import styles from "./DocumentsManager.module.css";
import PreviewPDF from "../Widgets/PreviewPDF";
import { getData } from "../../Services/getData";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { AxiosResponse } from "axios";
import useCustomMemo from "../../Hooks/useCustomMemo";
import withRole from "../../Hooks/withRole";
import { getFromCacheOrFetch } from "../../Utils/getFromCacheOrFetch";
import { fetchAllDocumentTypes } from "../../Utils/Utils";

function DocumentsManager(props: IDocumentsManagerProps) {
  const {
    orderId,
    clientData,
    productData,
    clientDocuments = [],
    setClientDocuments,
    triggerReload: externalTriggerReload,
    setTriggerReload: externalSetTriggerReload,
  } = props;
  const [contextValue, setContextValue] = useState(new DocumentsModel());
  const [internalTriggerReload, setInternalTriggerReload] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<DocumentsModel | null>(
    null
  );
  const [allDocumentTypes, setAllDocumentTypes] = useState<any[]>([]);
  const formRef = useRef<HTMLFormElement>(null);

  // Use external trigger if provided, otherwise use internal
  const triggerReload =
    externalTriggerReload !== undefined
      ? externalTriggerReload
      : internalTriggerReload;
  const setTriggerReload = externalSetTriggerReload || setInternalTriggerReload;
  const memo = useCustomMemo();
  const allStatuses = memo?.storage?.orderStatuses;

  // Callback to update documents and trigger reload
  const handleDocumentsUpdate = () => {
    if (setClientDocuments && clientData?._id && orderId) {
      getData(
        endpoints.client.getClientDocuments
          .replace(":clientId", clientData._id)
          .replace(":orderId", orderId)
      ).then((docsRes: AxiosResponse) => {
        if (docsRes?.data) {
          setClientDocuments(docsRes.data);
        }
      });
    }
    setTriggerReload((prev: boolean) => !prev);
  };

  // Fetch document types from cache
  useEffect(() => {
    let mounted = true;

    getFromCacheOrFetch<any[]>({
      cacheKey: "allDocumentTypes",
      storage: memo.storage,
      setStorage: memo.setStorageData,
      fetcher: fetchAllDocumentTypes(),
      isValid: (data) => Array.isArray(data) && data.length > 0,
    }).then((data) => {
      if (mounted) {
        setAllDocumentTypes(data);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  // Get required document types from product
  const requiredDocumentTypes = useMemo(() => {
    return productData?.documentTypes || [];
  }, [productData]);

  // Find missing required documents
  const missingRequiredDocuments = useMemo(() => {
    if (!requiredDocumentTypes.length) return [];

    const existingDocTypeIds = clientDocuments.map(
      (doc) => (doc.documentTypeId as any)?._id || doc.documentTypeId
    );

    return requiredDocumentTypes
      .filter((reqTypeId: string) => !existingDocTypeIds.includes(reqTypeId))
      .map((typeId: string) => {
        const docType = allDocumentTypes.find((dt) => dt._id === typeId);
        return {
          _id: `missing-${typeId}`,
          documentTypeId: docType,
          expirationDate: null,
          isMissing: true,
        };
      });
  }, [requiredDocumentTypes, clientDocuments, allDocumentTypes]);

  // Handle adding document with pre-populated type
  const handleAddDocument = (documentTypeId?: string) => {
    if (documentTypeId) {
      setContextValue({ ...new DocumentsModel(), documentTypeId });
    } else {
      setContextValue(new DocumentsModel());
    }
    setOpenForm(true);
  };

  /**
   * Define the columns for the documents table
   */
  const columns = [
    {
      name: "documentTypeId",
      label: Vocabulary.documentType,
      options: {
        customBodyRender: (value: any) => {
          return value?.name || "-";
        },
      },
    },
    {
      name: "expirationDate",
      label: Vocabulary.expirationDocumentDate,
      options: {
        customBodyRender: (value: Date | string | null, tableMeta: any) => {
          const rowData = tableMeta.tableData[tableMeta.rowIndex];
          if (rowData?.isMissing) return "-";
          if (!value) return "-";
          const dateObj = new Date(value);
          return dateObj.toLocaleDateString("ro-RO");
        },
      },
    },
  ];

  return (
    <Box>
      {/* Documents Status Alert */}
      {missingRequiredDocuments.length > 0 ? (
        <Alert
          severity="error"
          icon={<WarningIcon />}
          sx={{ mb: 2 }}
          className={styles.missingAlert}
        >
          <strong>
            {replacePlaceholders(Vocabulary.missingRequiredDocuments, {
              value: missingRequiredDocuments.length,
            })}
          </strong>
        </Alert>
      ) : requiredDocumentTypes.length > 0 ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>{Vocabulary.allRequiredDocumentsComplete}</strong>
        </Alert>
      ) : null}

      <MUIDataTableCustomStyle
        title={Vocabulary.documents}
        columns={columns}
        CRUDEndPoint={endpoints.client.getClientDocuments
          .replace(":clientId", clientData?._id || "")
          .replace(":orderId", orderId || "")}
        onEditAction={ETableEditAction.MODAL}
        setContextValue={setContextValue}
        triggerReload={triggerReload}
        selectableRows={"none"}
        handleModalForm={setOpenForm}
        setTriggerReload={handleDocumentsUpdate}
        rowProps={() => {
          return {};
        }}
        hideSearch={true}
        hideDeleteButton={!props?.delete}
        buttons={
          props?.showGenerateDocumentButton !== false && (
            <GenerateDocumentDropdown
              orderId={orderId}
              clientData={clientData}
              onDocumentGenerated={handleDocumentsUpdate}
            />
          )
        }
        renderCustomColumns={(_rowIndex: number, rowData: any) => {
          const handlePrint = async () => {
            if (!rowData?.document) return;

            // Always fetch the file, do not use direct URL
            const url = rowData.document.startsWith("http")
              ? rowData.document
              : `${window.location.origin}/${rowData.document.replace(
                  /^\/+/,
                  ""
                )}`;

            try {
              const response = await fetch(url, { credentials: "include" });
              if (!response.ok) throw new Error("Document not found");
              const blob = await response.blob();
              const objectUrl = URL.createObjectURL(blob);

              if (/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(url)) {
                // Print image from blob
                const printHtml = `<!DOCTYPE html><html><head><title>Print</title><style>body{margin:0;padding:0;text-align:center;}img{max-width:100vw;max-height:100vh;}</style></head><body><img src='${objectUrl}' onload='window.print();window.close();' /></body></html>`;
                const printWindow = window.open("", "_blank");
                if (printWindow) {
                  printWindow.document.open();
                  printWindow.document.write(printHtml);
                  printWindow.document.close();
                }
              } else if (/\.pdf$/i.test(url)) {
                // Open PDF in a new tab for manual print
                window.open(objectUrl, "_blank");
              } else {
                alert("Unsupported file type for printing.");
                URL.revokeObjectURL(objectUrl);
              }
            } catch (err) {
              alert("Document not found or cannot be printed.");
            }
          };

          return (
            <>
              <Tooltip title={Vocabulary.preview}>
                <ToggleButton
                  value="preview"
                  aria-label="preview"
                  onClick={() => setPreviewDocument(rowData)}
                >
                  <VisibilityIcon />
                </ToggleButton>
              </Tooltip>
              {props?.showPrintButton !== false && (
                <Tooltip title={Vocabulary.print}>
                  <ToggleButton
                    value="print"
                    aria-label="print"
                    onClick={handlePrint}
                  >
                    <PrintIcon />
                  </ToggleButton>
                </Tooltip>
              )}
            </>
          );
        }}
        customFooter={() => {
          if (missingRequiredDocuments.length === 0) return null;

          return (
            <>
              {missingRequiredDocuments.map((missingDoc: any) => (
                <TableRow
                  key={missingDoc._id}
                  className={styles.missingDocumentRow}
                >
                  <TableCell>
                    <input type="checkbox" disabled style={{ opacity: 0.3 }} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ErrorOutlineIcon
                        sx={{ color: "#d32f2f", fontSize: 20 }}
                      />
                      <span style={{ color: "#d32f2f", fontWeight: 500 }}>
                        {missingDoc.documentTypeId?.name} - Lipsește
                      </span>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<AddCircleIcon />}
                      onClick={() =>
                        handleAddDocument(missingDoc.documentTypeId?._id)
                      }
                    >
                      {Vocabulary.add}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </>
          );
        }}
        addOrEditForm={
          <GenericModal
            title={Vocabulary.documents}
            open={openForm}
            onClose={() => {
              setContextValue(new DocumentsModel());
              setOpenForm(false);
            }}
            children={
              <AddDocumentModal
                onClose={() => {
                  setContextValue(new DocumentsModel());
                  setOpenForm(false);
                  handleDocumentsUpdate();
                }}
                formRef={formRef}
                editData={contextValue}
                clientId={clientData?._id}
                orderId={orderId}
                requiredDocumentTypes={requiredDocumentTypes}
                existingDocuments={clientDocuments}
                allStatuses={allStatuses}
              />
            }
            actions={
              <div className={genericStyles.editClientModalEditButton}>
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => setOpenForm(false)}
                  startIcon={<CancelIcon />}
                >
                  {Vocabulary.cancel}
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  form={FormsIds.documentsForm}
                  color="success"
                  startIcon={<SaveIcon />}
                >
                  {Vocabulary.save}
                </Button>
              </div>
            }
          />
        }
      />

      {previewDocument && (
        <PreviewPDF
          title={
            (previewDocument.documentTypeId as any)?.name ||
            Vocabulary.document ||
            ""
          }
          open={!!previewDocument}
          onClose={() => setPreviewDocument(null)}
          url={"/" + previewDocument.document}
          htmlContent={""}
          hideDownloadButton={props?.hideDownloadButton}
          hidePrintButton={props?.hidePrintButton}
        />
      )}
    </Box>
  );
}

export default withRole(DocumentsManager);
